import { useEffect, useState } from 'react'
import toast, { Toast } from 'react-hot-toast'
import { forgetUndo, rememberUndo } from '@/src/renderer/lib/undo'
import { ToastCard, secondsLeft, useToastPausedAt } from '../Toast'
import type { UndoToastOptions } from './UndoToast.types'

/** Der Toast mit Countdown am Knopf („Rückgängig 5 s“, #223) — steht still, solange der Zeiger darüber ist. */
function UndoToastCard({ t, options }: { t: Toast; options: UndoToastOptions }) {
  const pausedAt = useToastPausedAt()
  const secondary = options.secondaryAction
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 250)
    return () => clearInterval(id)
  }, [])

  return (
    <ToastCard
      kind="undo"
      title={options.message}
      description={options.description}
      icon={options.icon}
      tone={options.tone}
      action={{
        label: options.undoLabel,
        onClick: async () => {
          // Der Knopf verbraucht die gemerkte Handlung — sonst nähme ⌘Z
          // danach dasselbe ein zweites Mal zurück.
          forgetUndo()
          toast.dismiss(t.id)
          await options.onUndo()
        },
      }}
      secondaryAction={secondary && {
        label: secondary.label,
        // Ohne `forgetUndo()`: Wer nur nachsieht, will ⌘Z behalten.
        onClick: async () => {
          toast.dismiss(t.id)
          await secondary.onClick()
        },
      }}
      secondsLeft={secondsLeft(t, pausedAt, now) ?? undefined}
      focusAction={options.focusAction}
      runsWhileFocused={options.focusAction}
      leaving={!t.visible}
      onDismiss={() => toast.dismiss(t.id)}
    />
  )
}

export function showUndoToast(options: UndoToastOptions) {
  const { onUndo, undoneMessage, duration = 6000 } = options
  const id = toast.custom((t: Toast) => <UndoToastCard t={t} options={options} />, { duration })

  /*
    **Dasselbe Angebot auch per Tastatur** (`lib/undo`, ADR 0028): `⌘Z` nimmt
    zurück, was hier steht — derselbe Weg, nur ohne Mausweg zur Meldung.

    **Ohne Frist**, anders als die Meldung: Die gemerkte Handlung gilt, bis sie
    verbraucht, ersetzt oder von einer anderen Änderung vergessen ist. Bleibt
    die Meldung stehen, während die Handlung schon vergessen ist, wirkt ihr
    Knopf trotzdem weiter — sie ist das Angebot für DIESE Löschung, `⌘Z` das
    für „deine letzte Änderung“.
  */
  rememberUndo(async () => {
    toast.dismiss(id)
    await onUndo()
  }, undoneMessage)

  return id
}
